import React, { useState, useEffect, useRef } from 'react';
import { Filter, Settings, Headphones, Zap, MoreHorizontal, BookOpen } from 'lucide-react';
import styles from './MobileActionsMenu.module.css';
import { useTranslation } from 'react-i18next';

interface MobileActionsMenuProps {
  isFilterPopupOpen: boolean;
  setIsFilterPopupOpen: (open: boolean) => void;
  isAuthorityPopupOpen: boolean;
  setIsAuthorityPopupOpen: (open: boolean) => void;
  isListeningMode: boolean;
  handleListeningModeChange: () => void;
  isFastSearch: boolean;
  setIsFastSearch: (fast: boolean) => void;
  isPersonalHoroscopeChat: boolean;
  citationMode: 'citation' | 'normal';
  onCitationModeChange: (mode: 'citation' | 'normal') => void;
  astroPromptMode: 'astro_standard_mode' | 'astro_advanced_mode';
  onAstroPromptModeChange: (mode: 'astro_standard_mode' | 'astro_advanced_mode') => void;
}

const MobileActionsMenu: React.FC<MobileActionsMenuProps> = ({
  isFilterPopupOpen,
  setIsFilterPopupOpen,
  isAuthorityPopupOpen,
  setIsAuthorityPopupOpen,
  isListeningMode,
  handleListeningModeChange,
  isFastSearch,
  setIsFastSearch,
  isPersonalHoroscopeChat,
  citationMode,
  onCitationModeChange,
  astroPromptMode,
  onAstroPromptModeChange
}) => {
  const { t } = useTranslation('chat');
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    // Haptic feedback on supported devices
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.top - 8, // 8px gap above trigger
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleFilterClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFilterPopupOpen(true);
    setIsOpen(false);
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAuthorityPopupOpen(true);
    setIsOpen(false);
  };

  const handleListeningClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleListeningModeChange();
    setIsOpen(false);
  };

  const handleCitationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPersonalHoroscopeChat) {
      const newMode = astroPromptMode === 'astro_advanced_mode' ? 'astro_standard_mode' : 'astro_advanced_mode';
      onAstroPromptModeChange(newMode);
    } else {
      const newMode = citationMode === 'citation' ? 'normal' : 'citation';
      onCitationModeChange(newMode);
    }
    setIsOpen(false);
  };

  const handleFastModeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newFastSearch = !isFastSearch;
    setIsFastSearch(newFastSearch);
    setIsOpen(false);
  };

  return (
    <div className={styles['mobile-actions-menu']} ref={menuRef}>
      <button
        ref={triggerRef}
        className={styles['mobile-menu-trigger']}
        onClick={handleToggle}
        aria-label="More options"
        type="button"
      >
        <MoreHorizontal size={20} />
      </button>
      
      {isOpen && dropdownPos && (
        <div
          className={styles['mobile-actions-dropdown']}
          style={{
            position: 'fixed',
            top: 'auto',
            bottom: `${window.innerHeight - dropdownPos.top}px`,
            right: `${dropdownPos.right}px`,
          }}
        >
          <button 
            onClick={handleFilterClick} 
            className={`${styles['mobile-action-item']} ${isFilterPopupOpen ? styles['text-blue-500'] : ''}`}
            type="button"
          >
            <Filter size={16} />
            <span>{t('chat.filters')}</span>
          </button>
          
          <button 
            onClick={handleSettingsClick} 
            className={`${styles['mobile-action-item']} ${isAuthorityPopupOpen ? styles['text-blue-500'] : ''}`}
            type="button"
          >
            <Settings size={16} />
            <span>{t('chat.authority_settings')}</span>
          </button>
          
          <button 
            onClick={handleListeningClick} 
            className={`${styles['mobile-action-item']} ${isListeningMode ? styles['text-yellow-500'] : ''}`}
            type="button"
          >
            <Headphones size={16} />
            <span>{t('chat.listening_mode')}</span>
          </button>
          
          <button
            onClick={handleFastModeClick}
            className={`${styles['mobile-action-item']} ${isFastSearch ? styles['text-blue-500'] : ''}`}
            type="button"
          >
            <Zap size={16} />
            <span>{t('chat.fast_mode')}</span>
          </button>

          <button
            onClick={handleCitationClick}
            className={`${styles['mobile-action-item']} ${
              (isPersonalHoroscopeChat ? astroPromptMode === 'astro_advanced_mode' : citationMode === 'citation')
                ? styles['text-blue-500'] : ''
            }`}
            type="button"
          >
            <BookOpen size={16} />
            <span>
              {isPersonalHoroscopeChat
                ? (astroPromptMode === 'astro_advanced_mode' ? t('chat.citation_toggle.detailed') : t('chat.citation_toggle.guidance'))
                : (citationMode === 'citation' ? t('chat.citation_toggle.citations') : t('chat.citation_toggle.normal'))
              }
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileActionsMenu;
